import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Flex, Heading, Text, Button, Stack, HStack, Icon, Spinner, Input, Image } from "@chakra-ui/react";
import { ArrowLeft, Archive, RotateCcw, Pencil, ImagePlus, Trash2, Users, AlertTriangle, MapPin } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { CompanyFeatures } from "@/components/CompanyFeatures";
import { CompanyUsers } from "@/components/CompanyUsers";
import { ConfirmModal } from "@/components/ConfirmModal";
import { CompanyAvatar, StatusPill, LOGO_TYPES, LOGO_MAX_MB } from "@/components/CompanyAvatar";
import { colors } from "@/theme/colors";

const Section = ({ title, children, testId, muted }) => (
  <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" overflow="hidden" data-testid={testId}>
    <Box px="5" py="3" bg={colors.tealTint} borderBottom="1px solid" borderColor="#BFE3E8">
      <Text fontFamily="heading" fontWeight="600" fontSize="xs" letterSpacing="0.09em" textTransform="uppercase" color={colors.tealHover}>{title}</Text>
    </Box>
    <Box p="5" opacity={muted ? 0.55 : 1} pointerEvents={muted ? "none" : "auto"}>{children}</Box>
  </Box>
);

const Field = ({ label, children }) => (
  <Box>
    <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>{label}</Text>
    {children}
  </Box>
);

const fmtWhen = (ts) => new Date(ts).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function PlatformCompany() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [userCount, setUserCount] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);
  const [labels, setLabels] = useState({});
  const [audit, setAudit] = useState([]);
  const [form, setForm] = useState({ name: "", industry: "", display_name: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'archive' | 'restore' | 'removeLogo'
  const [confirmBusy, setConfirmBusy] = useState(false);
  const fileRef = useRef(null);
  const profileRef = useRef(null);
  const flash = (type, text) => { setMsg({ type, text }); if (type !== "error") setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    const [{ data: comp }, { count }, { data: cat }] = await Promise.all([
      supabase.from("companies").select("*").eq("id", id).single(),
      supabase.from("memberships").select("user_id", { count: "exact", head: true }).eq("company_id", id),
      supabase.from("feature_catalog").select("key,label"),
    ]);
    setCompany(comp || null);
    setUserCount(count || 0);
    setLabels(Object.fromEntries((cat || []).map((f) => [f.key, f.label])));
    if (comp?.logo_path) {
      const { data } = await supabase.storage.from("company-logos").createSignedUrl(comp.logo_path, 3600);
      setLogoUrl(data?.signedUrl || null);
    } else setLogoUrl(null);
    setLoading(false);
  }, [id]);

  const loadAudit = useCallback(async () => {
    const { data: rows } = await supabase.from("feature_audit").select("*")
      .eq("company_id", id).eq("tier", 1).order("created_at", { ascending: false }).limit(8);
    const actorIds = [...new Set((rows || []).map((r) => r.actor).filter(Boolean))];
    let names = {};
    if (actorIds.length) {
      const { data: profs } = await supabase.from("profiles").select("id,full_name,email").in("id", actorIds);
      (profs || []).forEach((p) => { names[p.id] = p.full_name || p.email; });
    }
    setAudit((rows || []).map((r) => ({ ...r, actor_name: r.actor ? (names[r.actor] || "Super-Admin") : "System" })));
  }, [id]);

  useEffect(() => { load(); loadAudit(); }, [load, loadAudit]);
  useEffect(() => {
    if (company) setForm({ name: company.name || "", industry: company.industry || "", display_name: company.display_name || "" });
  }, [company]);

  const archived = company?.active === false;

  const saveProfile = async () => {
    if (!form.name.trim()) { flash("error", "Company name is required."); return; }
    setSaving(true);
    const { error } = await supabase.from("companies").update({
      name: form.name.trim(),
      industry: form.industry.trim() || null,
      display_name: form.display_name.trim() || null,
    }).eq("id", id);
    setSaving(false);
    if (error) { flash("error", error.message); return; }
    flash("success", "Profile saved.");
    load();
  };

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!LOGO_TYPES.test(file.type)) { flash("error", "Logo must be a PNG, JPG or WEBP image."); return; }
    if (file.size > LOGO_MAX_MB * 1024 * 1024) { flash("error", `Logo must be under ${LOGO_MAX_MB} MB.`); return; }
    setUploading(true);
    try {
      const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
      const path = `${id}/logo-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("company-logos").upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { error: ue } = await supabase.from("companies").update({ logo_path: path }).eq("id", id);
      if (ue) throw ue;
      if (company.logo_path) supabase.storage.from("company-logos").remove([company.logo_path]).catch(() => {});
      flash("success", "Logo updated.");
      load();
    } catch (err) { flash("error", err.message); }
    setUploading(false);
  };

  const runConfirm = async () => {
    setConfirmBusy(true);
    if (confirm === "removeLogo") {
      const { error } = await supabase.from("companies").update({ logo_path: null }).eq("id", id);
      if (!error && company.logo_path) supabase.storage.from("company-logos").remove([company.logo_path]).catch(() => {});
      setConfirmBusy(false); setConfirm(null);
      if (error) { flash("error", error.message); return; }
      flash("success", "Logo removed.");
    } else {
      const archiving = confirm === "archive";
      const { error } = await supabase.from("companies")
        .update({ active: !archiving, archived_at: archiving ? new Date().toISOString() : null }).eq("id", id);
      setConfirmBusy(false); setConfirm(null);
      if (error) { flash("error", error.message); return; }
      flash("success", archiving ? `${company.name} deactivated — its users are now locked out.` : `${company.name} restored — access re-enabled.`);
    }
    load();
  };

  const CONFIRM_COPY = {
    archive: {
      title: `Deactivate ${company?.name}?`,
      body: `This immediately locks out its ${userCount} ${userCount === 1 ? "user" : "users"}. Their data is retained and you can restore access at any time.`,
      label: "Deactivate", danger: true,
    },
    restore: {
      title: `Restore ${company?.name}?`,
      body: `This re-enables access for its ${userCount} ${userCount === 1 ? "user" : "users"} immediately.`,
      label: "Restore", danger: false,
    },
    removeLogo: {
      title: "Remove company logo?",
      body: "The logo will no longer appear beside the Hakina brand for this company's users. You can upload a new one at any time.",
      label: "Remove logo", danger: true,
    },
  };
  const cc = confirm ? CONFIRM_COPY[confirm] : null;

  if (loading) return <Flex minH="40vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;
  if (!company) return (
    <Box data-testid="company-not-found">
      <Button variant="ghost" size="sm" onClick={() => navigate("/platform")} data-testid="back-to-platform"><ArrowLeft size={15} /> All companies</Button>
      <Text mt="4" color={colors.textSecondary}>Company not found.</Text>
    </Box>
  );

  return (
    <Box data-testid="company-detail-page">
      <Button variant="ghost" size="sm" mb="3" onClick={() => navigate("/platform")} data-testid="back-to-platform">
        <ArrowLeft size={15} /> All companies
      </Button>

      {msg && (
        <Box mb="4" px="4" py="2.5" borderRadius="10px" fontSize="sm" bg={msg.type === "error" ? colors.criticalBg : colors.resolvedBg} color={msg.type === "error" ? colors.criticalText : colors.resolvedText} data-testid="company-detail-banner">{msg.text}</Box>
      )}

      {archived && (
        <Flex mb="4" px="4" py="3" borderRadius="12px" bg={colors.poppyTint} border="1px solid" borderColor={colors.poppy}
          align="center" gap="3" flexWrap="wrap" data-testid="suspended-banner">
          <Icon as={AlertTriangle} color={colors.poppy} boxSize="4.5" flexShrink={0} />
          <Text fontSize="sm" color={colors.textPrimary} flex="1" minW="200px">
            This company is <b>deactivated</b> — its users are locked out. All data is retained.
          </Text>
          <Button size="sm" onClick={() => setConfirm("restore")} data-testid="suspended-restore-btn"><RotateCcw size={14} /> Restore access</Button>
        </Flex>
      )}

      <Flex align="center" gap="4" mb="6" flexWrap="wrap">
        <CompanyAvatar name={company.name} logoUrl={logoUrl} size={64} />
        <Box flex="1" minW="200px">
          <HStack gap="3" flexWrap="wrap">
            <Heading fontFamily="heading" fontWeight="600" fontSize={{ base: "xl", md: "2xl" }} color={colors.textPrimary} data-testid="detail-company-name">{company.name}</Heading>
            <StatusPill archived={archived} testId="detail-status-pill" />
          </HStack>
          <HStack gap="2" mt="1" color={colors.textSecondary}>
            {company.industry && <Text fontSize="sm">{company.industry}</Text>}
            {company.industry && <Text fontSize="sm">·</Text>}
            <HStack gap="1"><Icon as={Users} boxSize="3.5" /><Text fontSize="sm" data-testid="detail-user-count">{userCount} {userCount === 1 ? "user" : "users"}</Text></HStack>
          </HStack>
        </Box>
        <HStack gap="2">
          <Button variant="outline" size="sm" data-testid="detail-edit-btn"
            onClick={() => profileRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}>
            <Pencil size={14} /> Edit
          </Button>
          {archived ? (
            <Button size="sm" onClick={() => setConfirm("restore")} data-testid="detail-restore-btn"><RotateCcw size={14} /> Restore</Button>
          ) : (
            <Button variant="danger" size="sm" onClick={() => setConfirm("archive")} data-testid="detail-archive-btn"><Archive size={14} /> Deactivate</Button>
          )}
        </HStack>
      </Flex>

      <Stack gap="5" maxW="760px">
        <Box ref={profileRef}>
          <Section title="Profile" testId="profile-section" muted={archived}>
            <Stack gap="4">
              <Flex gap="4" align="flex-start" flexWrap="wrap">
                <Flex align="center" justify="center" h="72px" w="144px" flexShrink={0} bg={colors.cream}
                  border="1px solid" borderColor={colors.border} borderRadius="10px" overflow="hidden" data-testid="detail-logo-box">
                  {logoUrl
                    ? <Image src={logoUrl} alt={`${company.name} logo`} maxH="64px" maxW="136px" objectFit="contain" data-testid="detail-logo-img" />
                    : <Text fontSize="xs" color={colors.textSecondary}>No logo</Text>}
                </Flex>
                <Stack gap="2">
                  <Button size="sm" variant="outline" loading={uploading} onClick={() => fileRef.current?.click()} data-testid="detail-upload-logo-btn">
                    <ImagePlus size={14} /> {company.logo_path ? "Replace logo" : "Upload logo"}
                  </Button>
                  {company.logo_path && (
                    <Button size="sm" variant="ghost" color={colors.poppy} onClick={() => setConfirm("removeLogo")} data-testid="detail-remove-logo-btn">
                      <Trash2 size={14} /> Remove
                    </Button>
                  )}
                  <Text fontSize="xs" color={colors.textSecondary}>PNG, JPG or WEBP · max {LOGO_MAX_MB} MB</Text>
                </Stack>
                <input type="file" accept="image/png,image/jpeg,image/webp" ref={fileRef} onChange={pickLogo}
                  data-testid="detail-logo-input" style={{ position: "absolute", width: 1, height: 1, opacity: 0 }} />
              </Flex>
              <Field label="Company name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} bg={colors.white} borderColor={colors.border} data-testid="detail-name-input" /></Field>
              <Field label="Industry"><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="e.g. Construction" bg={colors.white} borderColor={colors.border} data-testid="detail-industry-input" /></Field>
              <Field label="Display name (shown beside the Hakina brand)"><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder={`Defaults to ${company.name}`} bg={colors.white} borderColor={colors.border} data-testid="detail-display-name-input" /></Field>
              <Flex justify="flex-end">
                <Button size="sm" loading={saving} onClick={saveProfile} data-testid="detail-save-profile-btn">Save profile</Button>
              </Flex>
            </Stack>
          </Section>
        </Box>

        <Section title="Plan & Entitlements" testId="entitlements-section" muted={archived}>
          <CompanyFeatures companyId={id} flash={(t, m) => { flash(t, m); setTimeout(loadAudit, 500); }} />
          <Box mt="5" pt="4" borderTop="1px solid" borderColor={colors.border}>
            <Text fontSize="xs" fontWeight="700" letterSpacing="0.06em" textTransform="uppercase" color={colors.textSecondary} mb="2">Recent changes</Text>
            {audit.length === 0 ? (
              <Text fontSize="sm" color={colors.textSecondary} data-testid="feature-audit-empty">No entitlement changes recorded yet.</Text>
            ) : (
              <Stack gap="1.5" data-testid="feature-audit-list">
                {audit.map((a) => (
                  <Flex key={a.id} gap="2" align="baseline" flexWrap="wrap" data-testid={`feature-audit-row-${a.id}`}>
                    <Text fontSize="xs" color={colors.textPrimary}>
                      <b>{labels[a.feature_key] || a.feature_key}</b> turned{" "}
                      <Box as="span" fontWeight="700" color={a.new_enabled ? colors.sageDeep : colors.poppy}>{a.new_enabled ? "ON" : "OFF"}</Box>
                      {" "}by {a.actor_name}
                    </Text>
                    <Text fontSize="xs" color={colors.textSecondary}>· {fmtWhen(a.created_at)}</Text>
                  </Flex>
                ))}
              </Stack>
            )}
          </Box>
        </Section>

        <Section title="Users" testId="users-section">
          <CompanyUsers companyId={id} companyName={company?.name} flash={flash} onChanged={load} />
        </Section>

        <Section title="White-Glove Setup" testId="whiteglove-section">
          <Text fontSize="sm" color={colors.textSecondary} mb="3">
            Stand up this client's job sites and field workers on their behalf. Everything you create belongs to this company under its own tenant, exactly as if their Org Admin entered it.
          </Text>
          <HStack gap="3" flexWrap="wrap">
            <Button size="sm" variant="outline" onClick={() => navigate(`/platform/company/${id}/setup?tab=locations`)} data-testid="manage-locations-btn">
              <MapPin size={14} /> Manage Locations
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate(`/platform/company/${id}/setup?tab=employees`)} data-testid="manage-employees-btn">
              <Users size={14} /> Manage Employees
            </Button>
          </HStack>
        </Section>
      </Stack>

      <ConfirmModal open={!!confirm} onClose={() => setConfirm(null)}
        title={cc?.title || ""} body={cc?.body || ""} confirmLabel={cc?.label || ""}
        danger={cc?.danger} busy={confirmBusy} onConfirm={runConfirm} />
    </Box>
  );
}
